import React, { useEffect } from "react";
import { useSnackbar } from '../contexts/SnackbarProvider';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Switch, Stack
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import axiosInstance from "../utils/axiosInstance";

const ContactModal = ({ hasLimit, open, onClose, contact, clientId, refetch }) => {
    const { showSnackbar } = useSnackbar();
    const isEdit = !!contact;
    const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            client_contact_name: "",
            client_contact_email: "",
            client_contact_phone: "",
            status: "pending"
        },
    });

    useEffect(() => {
        if (contact) {
            reset({
                client_contact_name: contact.client_contact_name || "",
                client_contact_email: contact.client_contact_email || "",
                client_contact_phone: contact.client_contact_phone || "",
                status: contact.status || "pending",
            });
        } else {
            reset({
                client_contact_name: "",
                client_contact_email: "",
                client_contact_phone: "",
                status: "pending",
            });
        }
    }, [contact, open, reset]);
    const handleSave = async (data) => {
        try {
            let res;
            if (isEdit) {
                // 編集
                res = await axiosInstance.put(
                    `/client/${clientId}/contact/${contact.client_contact_id}`,
                    data
                );
            } else {
                // 追加
                res = await axiosInstance.post(
                    `/client/${clientId}/contact`,
                    data
                );
            }
            refetch();
            showSnackbar('担当者情報を保存しました', 'success');
            onClose();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '保存に失敗しました', 'error');
        }
    };

    const handleDelete = async () => {
        if (!contact?.client_contact_id) return;
        if (!window.confirm("担当者を削除しますか？")) return;

        try {
            const payload = { ...contact, status: "invalid" };
            const res = await axiosInstance.put(`/client/${clientId}/contact/${contact.client_contact_id}`, payload);
            refetch();
            showSnackbar(res.data?.message || '削除しました', 'success');
            onClose();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
        }
    };

    const handleSendMail = async () => {
        if (!contact?.client_contact_id) return;
        try {
            const res = await axiosInstance.post(
                `/client/${clientId}/contact/${contact.client_contact_id}/send-mail`
            );
            refetch();
            showSnackbar(res.data?.message || 'メールを送信しました', 'success');
            onClose();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || 'メール送信に失敗しました', 'error');
        }
    };


    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? "担当者編集" : "担当者追加"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <Controller
                        name="client_contact_name"
                        control={control}
                        rules={{
                            required: "担当者名は必須です",
                            maxLength: { value: 100, message: "100文字以内で入力してください" }
                        }}
                        render={({ field }) => (
                            <TextField
                                label="担当者名"
                                fullWidth
                                {...field}
                                error={!!errors.client_contact_name}
                                helperText={errors.client_contact_name?.message}
                                inputProps={{ maxLength: 100 }}
                                multiline
                            />
                        )}
                    />
                    <Controller
                        name="client_contact_email"
                        control={control}
                        rules={{
                            required: "メールアドレスは必須です",
                            maxLength: { value: 255, message: "255文字以内で入力してください" },
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: "メール形式が正しくありません"
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                label="メールアドレス"
                                fullWidth
                                {...field}
                                error={!!errors.client_contact_email}
                                helperText={errors.client_contact_email?.message}
                                inputProps={{ maxLength: 255 }}
                                multiline
                            />
                        )}
                    />
                    <Controller
                        name="client_contact_phone"
                        control={control}
                        rules={{
                            maxLength: { value: 20, message: "20文字以内で入力してください" },
                            pattern: {
                                value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/,
                                message: "電話番号の形式が正しくありません"
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                label="電話番号"
                                fullWidth
                                {...field}
                                error={!!errors.client_contact_phone}
                                helperText={errors.client_contact_phone?.message}
                                inputProps={{ maxLength: 20 }}
                            />
                        )}
                    />

                    {/* 編集モード時のみステータス切替 */}
                    {isEdit && (
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={field.value === "active"}
                                            onChange={e => field.onChange(e.target.checked ? "active" : "pending")}
                                        />
                                    }
                                    label="有効"
                                />
                            )}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                {isEdit && (
                    <Button color="error" onClick={handleDelete} disabled={isSubmitting} variant="outlined" sx={{ mr: 'auto' }}>
                        削除する
                    </Button>
                )}
                {/* メール送信ボタン */}
                {hasLimit('ec') && isEdit && (
                    <Button
                        onClick={handleSendMail}
                        disabled={isSubmitting}
                        variant="outlined"
                        sx={{ mr: 'auto' }}
                    >
                        メール送信
                    </Button>
                )}
                <Button onClick={onClose} disabled={isSubmitting} variant="outlined">キャンセル</Button>
                <Button variant="contained" onClick={handleSubmit(handleSave)} disabled={isSubmitting}>
                    保存
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContactModal;